import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Heading2, Heading3, Pilcrow, Quote, List, ListOrdered, Link2, ImagePlus, RemoveFormatting } from "lucide-react";
import { adminUpload, formatApiErrorDetail, resolveImage } from "@/lib/adminApi";

const Btn = ({ title, onClick, children, testId }) => (
  <button type="button" className="rte__btn" title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testId}>
    {children}
  </button>
);

// Minimal rich-text editor built on contentEditable. Emits HTML.
export default function RichEditor({ value, onChange, onError }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== (value || "") && document.activeElement !== el) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const cmd = (name, arg) => {
    ref.current?.focus();
    document.execCommand(name, false, arg);
    emit();
  };

  const block = (tag) => cmd("formatBlock", tag);

  const link = () => {
    const url = window.prompt("Link address (https://…)");
    if (!url) return;
    cmd("createLink", url);
  };

  const image = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminUpload(file);
      cmd("insertImage", resolveImage(url));
    } catch (e) {
      onError?.(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rte" data-testid="rich-editor">
      <div className="rte__bar" role="toolbar" aria-label="Formatting">
        <Btn title="Paragraph" onClick={() => block("p")} testId="rte-p"><Pilcrow size={15} /></Btn>
        <Btn title="Heading" onClick={() => block("h2")} testId="rte-h2"><Heading2 size={15} /></Btn>
        <Btn title="Subheading" onClick={() => block("h3")} testId="rte-h3"><Heading3 size={15} /></Btn>
        <span className="rte__sep" />
        <Btn title="Bold" onClick={() => cmd("bold")} testId="rte-bold"><Bold size={15} /></Btn>
        <Btn title="Italic" onClick={() => cmd("italic")} testId="rte-italic"><Italic size={15} /></Btn>
        <Btn title="Quote" onClick={() => block("blockquote")} testId="rte-quote"><Quote size={15} /></Btn>
        <span className="rte__sep" />
        <Btn title="Bullet list" onClick={() => cmd("insertUnorderedList")} testId="rte-ul"><List size={15} /></Btn>
        <Btn title="Numbered list" onClick={() => cmd("insertOrderedList")} testId="rte-ol"><ListOrdered size={15} /></Btn>
        <Btn title="Link" onClick={link} testId="rte-link"><Link2 size={15} /></Btn>
        <label className="rte__btn" title="Insert image" data-testid="rte-image">
          <ImagePlus size={15} />
          <input type="file" accept="image/*" hidden onChange={(e) => { image(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        <span className="rte__sep" />
        <Btn title="Clear formatting" onClick={() => cmd("removeFormat")} testId="rte-clear"><RemoveFormatting size={15} /></Btn>
        {busy && <span className="rte__status mono-caps">Uploading…</span>}
      </div>
      <div
        ref={ref}
        className="rte__area post-body"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder="Write your post here…"
        data-testid="rte-area"
      />
    </div>
  );
}
